import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Lightning, MapPin, User, CheckCircle, Warning, Truck, X, PaintBrushBroad, Users as UsersIcon, ArrowRight, ArrowLeft, Ghost, Plus, Sun, Cloud, CloudRain, CloudSnow, CloudLightning, ArrowsClockwise, Wrench } from "@phosphor-icons/react";
import { TriageMenu } from "@/components/TriageMenu";
import SweeperWoPicker from "@/components/SweeperWoPicker";
import { ReturnBadge } from "@/components/ReturnLater";

function SweepJobRow({ j, dropped, onToggle, onTriaged, children, peek = false }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dx, setDx] = useState(0);
  const start = useRef(null);
  const onTouchStart = (e) => { start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchMove = (e) => {
    if (!start.current) return;
    const ddx = e.touches[0].clientX - start.current.x, ddy = Math.abs(e.touches[0].clientY - start.current.y);
    if (ddy > 40) { start.current = null; setDx(0); return; }
    if (ddx < 0) setDx(Math.max(ddx, -96));
  };
  const onTouchEnd = () => {
    if (dx <= -60) setMenuOpen(true);
    start.current = null; setDx(0);
  };
  return (
    <div className={`relative overflow-hidden ${peek ? "sweep-peek" : ""}`} data-testid={`lineup-job-${j.id}`}>
      <div className="absolute inset-y-0 right-0 w-24 bg-red-600 text-white flex items-center justify-center gap-1 text-[10px] font-black uppercase tracking-widest" aria-hidden>
        ⋮ Triage
      </div>
      <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        style={{ transform: `translateX(${dx}px)`, transition: dx === 0 ? "transform 160ms ease-out" : "none" }}
        className={`sweep-row relative p-2 border-2 flex items-start gap-2 ${dropped ? "border-slate-200 bg-slate-100 opacity-50" : "border-slate-300 bg-white hover:border-[#00C4CC]"}`}>
        <button onClick={onToggle}
          className={`h-6 w-6 border-2 flex-shrink-0 mt-0.5 flex items-center justify-center font-black text-xs ${dropped ? "border-slate-400 bg-white text-slate-400" : "border-[#0f172a] bg-[#00C4CC] text-white"}`}
          data-testid={`lineup-toggle-${j.id}`}>
          {dropped ? "+" : "✓"}
        </button>
        {children}
        <TriageMenu jobId={j.id} sweeper onDone={onTriaged}/>
        {menuOpen && <TriageMenu jobId={j.id} sweeper open onClose={() => setMenuOpen(false)} onDone={onTriaged}/>}
      </div>
    </div>
  );
}

function tomorrowISO() {
  const d = new Date(); d.setDate(d.getDate() + 1);
  const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function yesterdayISO() {
  const d = new Date(); d.setDate(d.getDate() - 1);
  const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const SPECIALTIES = [
  { value: "", label: "General", icon: Truck, cls: "bg-slate-100 text-slate-700 border-slate-300" },
  { value: "painting", label: "Painting only", icon: PaintBrushBroad, cls: "bg-amber-100 text-amber-800 border-amber-400" },
  { value: "handyman", label: "Handyman · small elec/plumb/drywall", icon: Wrench, cls: "bg-emerald-100 text-emerald-800 border-emerald-400" },
];

export default function AdminLineup() {
  const [date, setDate] = useState(tomorrowISO());
  const [capacity, setCapacity] = useState(8);
  const [workers, setWorkers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [step, setStep] = useState(1); // 1=roll-call, 2=trucks, 3=preview
  const [rollCall, setRollCall] = useState({}); // {worker_id: true/false}
  const [crews, setCrews] = useState([]); // [{lead_id, helper_id, cap, specialty}]
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [drops, setDrops] = useState({});
  const [actuals, setActuals] = useState([]);
  const [weather, setWeather] = useState(null);
  const [tapPins, setTapPins] = useState([]);
  const [picker, setPicker] = useState(null); // {truckKey, truckLabel}
  const [showTyped, setShowTyped] = useState({});

  const loadTapPins = () => api.get(`/jobs/sweeper-pins?date=${date}`).then(r => setTapPins(r.data || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTapPins(); }, [date, step]);
  const unpin = async (jobId) => {
    try { await api.post("/jobs/sweeper-unpin", { job_ids: [jobId] }); toast.success("Unpinned"); loadTapPins(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not unpin")); }
  };

  useEffect(() => {
    api.get("/assignable-users").then(r => {
      const ws = (r.data || []).filter(u => u.role === "worker");
      setWorkers(ws);
      // Default: everyone is ON
      const rc = {};
      ws.forEach(w => { rc[w.id] = true; });
      setRollCall(rc);
    }).catch(() => {});
    api.get(`/lineup/actuals?date=${yesterdayISO()}`).then(r => setActuals(r.data?.workers || [])).catch(() => {});
    api.get("/sub-contractors").then(r => setSubs(r.data || [])).catch(() => {});
    api.get("/weather/forecast?days=7").then(r => setWeather(r.data)).catch(() => {});
  }, []);

  const workingWorkers = workers.filter(w => rollCall[w.id]);

  const proceedToTrucks = () => {
    if (workingWorkers.length === 0) toast.message("No in-house techs today — add a sub truck on the next step");
    // Build default crews: one solo truck per working worker, unless we've already built them
    const existingLeadIds = new Set(crews.map(c => c.lead_id));
    const nextCrews = crews.filter(c => rollCall[c.lead_id] && (!c.helper_id || rollCall[c.helper_id]));
    workingWorkers.forEach(w => {
      if (!nextCrews.some(c => c.lead_id === w.id || c.helper_id === w.id)) {
        nextCrews.push({ lead_id: w.id, helper_id: "", cap: capacity, specialty: "" });
      }
    });
    setCrews(nextCrews);
    setStep(2);
  };

  const generate = async () => {
    setLoading(true);
    try {
      const payload = crews.map(c => {
        if (c.phantom_name || (typeof c.lead_id === "string" && c.lead_id.startsWith("phantom-"))) {
          return { phantom_name: c.phantom_name || "", subcontractor_id: c.subcontractor_id || "", cap: c.cap, specialty: c.specialty || "", pinned_wos: c.pinned_wos || "" };
        }
        return { lead_id: c.lead_id, helper_id: c.helper_id, cap: c.cap, specialty: c.specialty || "", pinned_wos: c.pinned_wos || "" };
      });
      const r = await api.post("/lineup/preview", { date, day_capacity: capacity, crews: payload });
      setPreview(r.data);
      setDrops({});
      setStep(3);
      toast.success(`Drafted ${r.data.plans.reduce((n, p) => n + p.jobs.length, 0)} jobs across ${r.data.plans.length} trucks`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not generate lineup"));
    } finally { setLoading(false); }
  };

  const [showPeek, setShowPeek] = useState(false);
  useEffect(() => {
    if (!preview || !("ontouchstart" in window)) return;
    if (localStorage.getItem("fr_sweep_peek_seen")) return;
    setShowPeek(true);
    localStorage.setItem("fr_sweep_peek_seen", "1");
    const t = setTimeout(() => setShowPeek(false), 2600);
    return () => clearTimeout(t);
  }, [preview]);

  const removeFromPreview = (jobId) => {
    setPreview(prev => prev ? { ...prev, plans: prev.plans.map(p => ({ ...p, jobs: p.jobs.filter(j => j.id !== jobId) })) } : prev);
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const assignments = preview.plans.map(p => p.is_phantom
        ? { phantom_name: p.sub_tradesman_name || "",
            subcontractor_id: p.subcontractor_id || "",
            job_ids: p.jobs.filter(j => !drops[j.id]).map(j => j.id) }
        : { lead_id: p.lead_id, helper_id: p.helper_id || undefined,
            job_ids: p.jobs.filter(j => !drops[j.id]).map(j => j.id) }
      ).filter(a => a.job_ids.length > 0);
      const r = await api.post("/lineup/commit", { date, assignments });
      toast.success(`Committed ${r.data.committed} jobs to ${date}`);
      setPreview(null);
      setStep(1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not commit lineup"));
    } finally { setCommitting(false); }
  };

  const addPhantomTruck = () => {
    setCrews([...crews, { lead_id: `phantom-${Date.now()}`, helper_id: "", cap: capacity, specialty: "", phantom_name: "", subcontractor_id: "" }]);
  };
  const setPhantomName = (idx, name) => {
    const next = [...crews]; next[idx] = { ...next[idx], phantom_name: name }; setCrews(next);
  };
  const setPhantomSub = (idx, subId) => {
    const sub = subs.find(s => s.id === subId);
    const spec = sub?.sweeper_specialty || "";
    const next = [...crews]; next[idx] = { ...next[idx], subcontractor_id: subId, ...(spec ? { specialty: spec } : {}) }; setCrews(next);
  };

  const setHelper = (idx, helper_id) => {
    const next = [...crews];
    next[idx] = { ...next[idx], helper_id };
    if (helper_id) {
      // Remove any other crew that had this helper as lead OR helper
      for (let i = next.length - 1; i >= 0; i--) {
        if (i === idx) continue;
        if (next[i].lead_id === helper_id) next.splice(i, 1);
        else if (next[i].helper_id === helper_id) next[i] = { ...next[i], helper_id: "" };
      }
      next[idx] = { ...next[idx], cap: next[idx].cap === capacity ? 12 : next[idx].cap };
    } else {
      next[idx] = { ...next[idx], cap: capacity };
    }
    // Re-add any working worker who's no longer on a truck as a solo truck
    const usedIds = new Set(next.flatMap(c => [c.lead_id, c.helper_id].filter(Boolean)));
    workingWorkers.forEach(w => {
      if (!usedIds.has(w.id)) next.push({ lead_id: w.id, helper_id: "", cap: capacity, specialty: "" });
    });
    setCrews(next);
  };
  const setCrewCap = (idx, val) => {
    const next = [...crews]; next[idx] = { ...next[idx], cap: Number(val) || 0 }; setCrews(next);
  };
  const setCrewSpecialty = (idx, val) => {
    const next = [...crews]; next[idx] = { ...next[idx], specialty: val }; setCrews(next);
  };
  const removeCrew = (idx) => {
    // Removing a truck also marks those workers as "not on today" — user made an explicit choice
    const c = crews[idx];
    const rc = { ...rollCall };
    if (c.lead_id) rc[c.lead_id] = false;
    if (c.helper_id) rc[c.helper_id] = false;
    setRollCall(rc);
    const next = [...crews]; next.splice(idx, 1); setCrews(next);
  };

  const nameOf = (id) => workers.find(w => w.id === id)?.name || "";

  return (
    <AdminShell>
      <span className="hidden" data-proman-context={`Sweeper Radar for ${date}, step ${step} (1=Roll Call, 2=Trucks, 3=Draft). ${crews.length} trucks, ${tapPins.length} tapped pins.`}/>
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          {/* v2 escape hatch — shown whenever this classic view is rendered so you always have a one-tap return */}
          <Link to="/admin/lineup" data-testid="classic-back-to-v2"
            className="inline-flex items-center gap-1.5 mb-3 text-xs font-bold text-[#008a8f] hover:text-[#0B1F3A] bg-[#E6FAFB] hover:bg-[#d0f5f7] px-3 py-1.5 rounded-full transition-colors">
            ← Back to whiteboard (new)
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight flex items-center gap-2">
              <Lightning weight="fill" size={30} className="text-[#00C4CC]"/> Sweeper Radar™
            </h1>
            <span className="text-xs font-black uppercase tracking-widest text-slate-500">SweepMode™ · Cluster jobs by proximity & plan the crew's day</span>
          </div>

          {/* Stepper */}
          <div className="mt-4 flex items-center gap-2" data-testid="lineup-stepper">
            <StepPill num={1} label="Roll Call" active={step === 1} done={step > 1} onClick={() => setStep(1)}/>
            <div className="flex-1 h-0.5 bg-slate-300"/>
            <StepPill num={2} label="Trucks" active={step === 2} done={step > 2} onClick={() => (workingWorkers.length > 0 && step > 1) && setStep(2)}/>
            <div className="flex-1 h-0.5 bg-slate-300"/>
            <StepPill num={3} label="Draft" active={step === 3} done={false} onClick={() => step === 3 && setStep(3)}/>
          </div>

          {/* Weather forecast strip */}
          {weather && weather.days && weather.days.length > 0 && (
            <WeatherStrip weather={weather} date={date} setDate={setDate} crews={crews} preview={preview}/>
          )}

          {/* Yesterday's actuals — always visible */}
          {actuals.length > 0 && (
            <div className="mt-4 rugged-card p-3 bg-slate-100" data-testid="yesterday-actuals">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">Yesterday's actuals ({yesterdayISO()})</div>
              <div className="flex flex-wrap gap-2">
                {actuals.map(a => {
                  const pct = a.planned > 0 ? Math.round((a.completed / a.planned) * 100) : 0;
                  return (
                    <div key={a.worker_id} data-testid={`actual-${a.worker_id}`}
                      className="px-2 py-1 border-2 border-[#0f172a] bg-white text-[11px] font-bold inline-flex items-center gap-2">
                      <span className="font-black uppercase">{a.worker_name}</span>
                      <span className={pct >= 80 ? "text-green-700" : pct >= 50 ? "text-amber-700" : "text-red-700"}>
                        {a.completed}/{a.planned} closed ({pct}%)
                      </span>
                      {a.follow_up > 0 && <span className="text-yellow-700">· {a.follow_up} follow-up</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 1 — Roll Call */}
          {step === 1 && (
            <div className="mt-5 rugged-card p-5 bg-white" data-testid="step-roll-call">
              <div className="flex items-center gap-2">
                <UsersIcon weight="fill" size={22}/>
                <h2 className="font-display font-black text-xl uppercase">Who's Working {date}?</h2>
              </div>
              <p className="mt-1 text-sm text-slate-600 font-medium">Tap a worker to mark them in or out for the day. Off = they won't get a truck.</p>

              <div className="mt-4 flex flex-wrap gap-2">
                {workers.map(w => {
                  const isIn = !!rollCall[w.id];
                  return (
                    <button key={w.id} onClick={() => setRollCall({ ...rollCall, [w.id]: !isIn })}
                      data-testid={`rollcall-${w.id}`}
                      className={`px-4 h-11 border-2 font-black uppercase text-xs tracking-widest inline-flex items-center gap-2 transition ${isIn ? "bg-[#00C4CC] text-white border-[#00a8af]" : "bg-slate-100 text-slate-500 border-slate-300 line-through opacity-70"}`}>
                      {isIn ? <CheckCircle weight="fill" size={16}/> : <X weight="bold" size={16}/>}
                      {w.name}
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
                <div className="text-xs font-black uppercase tracking-widest">
                  <span className="text-[#00C4CC]" data-testid="rollcall-count">{workingWorkers.length}</span>
                  <span className="text-slate-500"> of {workers.length} in today</span>
                </div>
                <button onClick={proceedToTrucks}
                  title={workingWorkers.length === 0 ? "No in-house techs today — continue to add sub trucks" : ""}
                  data-testid="rollcall-next"
                  className="tactile-btn h-11 px-5 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50">
                  Next: Build Trucks <ArrowRight weight="bold" size={14}/>
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 — Truck Assembly */}
          {step === 2 && (
            <div className="mt-5 rugged-card p-5 bg-white" data-testid="step-trucks">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Truck weight="fill" size={22}/>
                  <h2 className="font-display font-black text-xl uppercase">Assemble Trucks</h2>
                </div>
                <button onClick={() => setStep(1)} data-testid="trucks-back"
                  className="h-9 px-3 border-2 border-slate-300 text-slate-600 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1 hover:bg-slate-50">
                  <ArrowLeft weight="bold" size={12}/> Roll Call
                </button>
              </div>
              <p className="mt-1 text-sm text-slate-600 font-medium">Pair helpers into 2-man trucks, adjust slots, and tag specialty crews (like a paint-only truck).</p>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {crews.map((c, idx) => {
                  const isPhantom = !!c.phantom_name || (typeof c.lead_id === "string" && c.lead_id.startsWith("phantom-"));
                  const availableHelpers = isPhantom ? [] : workingWorkers.filter(w => w.id !== c.lead_id && !crews.some((cc, i) => i !== idx && (cc.lead_id === w.id || cc.helper_id === w.id)));
                  const specialtyInfo = SPECIALTIES.find(s => s.value === (c.specialty || ""));
                  const SpecialtyIcon = specialtyInfo?.icon || Truck;
                  const truckKey = isPhantom ? `sub:${c.subcontractor_id || ""}` : c.lead_id;
                  const truckPins = tapPins.filter(p => p.truck_key === truckKey);
                  return (
                    <div key={c.lead_id} className={`p-3 border-2 ${isPhantom ? "border-fuchsia-400 bg-fuchsia-50" : c.specialty === "painting" ? "border-amber-400 bg-amber-50" : c.helper_id ? "border-[#00C4CC] bg-cyan-50" : "border-slate-300 bg-slate-50"}`} data-testid={`truck-${c.lead_id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        {isPhantom
                          ? <Ghost weight="fill" size={18} className="text-fuchsia-600"/>
                          : <SpecialtyIcon weight="fill" size={18} className={c.specialty === "painting" ? "text-amber-700" : c.helper_id ? "text-[#00C4CC]" : "text-slate-600"}/>}
                        {isPhantom ? (
                          <div className="flex-1 flex flex-col gap-1.5">
                            <select
                              value={c.subcontractor_id || ""}
                              onChange={(e) => setPhantomSub(idx, e.target.value)}
                              data-testid={`phantom-sub-${c.lead_id}`}
                              className="h-8 px-2 border border-fuchsia-400 text-xs font-black uppercase bg-white">
                              <option value="">— Free text (no sub) —</option>
                              {subs.map(s => <option key={s.id} value={s.id}>{s.name}{s.trade ? ` · ${s.trade}` : ""}</option>)}
                            </select>
                            <input
                              type="text"
                              placeholder="Tradesman name (optional)"
                              value={c.phantom_name || ""}
                              onChange={(e) => setPhantomName(idx, e.target.value)}
                              data-testid={`phantom-name-${c.lead_id}`}
                              className="h-8 px-2 border border-fuchsia-400 text-sm font-black uppercase bg-white"
                            />
                          </div>
                        ) : (
                          <>
                            <span className="text-sm font-black uppercase">{nameOf(c.lead_id)}</span>
                            {c.helper_id ? (
                              <>
                                <span className="text-slate-500">+</span>
                                <span className="text-sm font-black uppercase text-[#0e7490]">{nameOf(c.helper_id)}</span>
                                <button onClick={() => setHelper(idx, "")} data-testid={`unpair-${c.lead_id}`}
                                  className="ml-auto text-slate-500 hover:text-red-600" title="Break into two 1-man trucks">
                                  <X weight="bold" size={14}/>
                                </button>
                              </>
                            ) : (
                              availableHelpers.length > 0 && (
                                <select value="" onChange={(e) => e.target.value && setHelper(idx, e.target.value)}
                                  data-testid={`pair-select-${c.lead_id}`}
                                  className="ml-auto h-8 px-2 border border-[#0f172a] text-[11px] font-bold bg-white">
                                  <option value="">+ Pair helper…</option>
                                  {availableHelpers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                </select>
                              )
                            )}
                          </>
                        )}
                        <button onClick={() => removeCrew(idx)} data-testid={`remove-truck-${c.lead_id}`}
                          className="ml-1 text-slate-400 hover:text-red-600" title={isPhantom ? "Remove phantom truck" : "Remove truck (marks workers Out)"}>
                          <X weight="bold" size={12}/>
                        </button>
                      </div>

                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Slots</label>
                        <input type="number" min="1" max="24" step="0.5" value={c.cap}
                          onChange={(e) => setCrewCap(idx, e.target.value)}
                          data-testid={`cap-${c.lead_id}`}
                          className="h-8 w-16 px-1 border border-[#0f172a] text-[11px] font-bold bg-white"/>
                        <span className="text-[10px] text-slate-400">{isPhantom ? "phantom · manual close" : c.helper_id ? "2-man default 12" : "1-man default 8"}</span>
                      </div>

                      <div className="mt-2 flex flex-wrap gap-1">
                        {SPECIALTIES.map(s => {
                          const active = (c.specialty || "") === s.value;
                          const Icon = s.icon;
                          return (
                            <button key={s.value} onClick={() => setCrewSpecialty(idx, s.value)}
                              data-testid={`spec-${c.lead_id}-${s.value || "general"}`}
                              className={`h-7 px-2 border-2 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 ${active ? s.cls : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}>
                              <Icon weight="fill" size={12}/> {s.label}
                            </button>
                          );
                        })}
                      </div>
                      {truckPins.length > 0 && (
                        <div className="mt-2 space-y-1" data-testid={`tap-pins-${c.lead_id}`}>
                          {truckPins.map(p => (
                            <div key={p.id} className="flex items-center gap-1.5 px-2 h-8 border-2 border-amber-400 bg-white text-xs font-bold min-w-0" data-testid={`tap-pin-${p.id}`}>
                              <span className="text-[9px] bg-amber-400 text-slate-900 px-1 font-black uppercase flex-shrink-0">📌 Pinned</span>
                              {p.return_visit?.date && <span className="text-[9px] bg-orange-500 text-white px-1 font-black uppercase flex-shrink-0">↩ Return</span>}
                              {p.work_order_number && <span className="font-mono text-[10px] text-slate-500 flex-shrink-0">{p.work_order_number}</span>}
                              <span className="truncate">{p.title}</span>
                              <button onClick={() => unpin(p.id)} data-testid={`tap-unpin-${p.id}`} title="Unpin"
                                className="ml-auto text-slate-400 hover:text-red-600 flex-shrink-0"><X weight="bold" size={12}/></button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="mt-2 flex items-center gap-2">
                        <button onClick={() => setPicker({ truckKey, truckLabel: isPhantom ? (subs.find(s => s.id === c.subcontractor_id)?.name || c.phantom_name || "Sub") : nameOf(c.lead_id) })}
                          disabled={isPhantom && !c.subcontractor_id}
                          title={isPhantom && !c.subcontractor_id ? "Pick a sub company first" : ""}
                          data-testid={`add-wos-${c.lead_id}`}
                          className="flex-1 h-11 border-2 border-slate-900 bg-amber-400 hover:bg-amber-300 text-slate-900 font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50">
                          📌 Add WOs {truckPins.length > 0 && <span className="bg-slate-900 text-white px-1.5 text-[10px]">{truckPins.length}</span>}
                        </button>
                        <button onClick={() => setShowTyped({ ...showTyped, [idx]: !showTyped[idx] })} data-testid={`toggle-typed-${idx}`}
                          className="h-11 px-2 text-[10px] font-bold text-slate-500 underline">type #s</button>
                      </div>
                      {(showTyped[idx] || c.pinned_wos) && <input value={c.pinned_wos || ""} data-testid={`pinned-wos-${idx}`}
                        onChange={(e) => { const next = [...crews]; next[idx] = { ...next[idx], pinned_wos: e.target.value }; setCrews(next); }}
                        placeholder="📌 Pin WO#s: 3635-1, 3277 (force onto this truck)"
                        className="mt-2 w-full h-8 px-2 border-2 border-dashed border-amber-400 bg-amber-50 text-xs font-mono placeholder:font-sans placeholder:text-amber-700/70"/>}
                    </div>
                  );
                })}
                {/* Add Phantom Truck tile */}
                <button onClick={addPhantomTruck} data-testid="add-phantom-truck"
                  className="p-3 border-2 border-dashed border-fuchsia-400 bg-white hover:bg-fuchsia-50 text-fuchsia-700 font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 min-h-[92px]">
                  <Ghost weight="fill" size={18}/> <Plus weight="bold" size={14}/> Add Sub Tradesman
                </button>
              </div>
              {picker && (
                <SweeperWoPicker truckKey={picker.truckKey} truckLabel={picker.truckLabel} date={date}
                  pinnedIds={new Set(tapPins.filter(p => p.truck_key === picker.truckKey).map(p => p.id))}
                  onChange={loadTapPins} onClose={() => setPicker(null)}/>
              )}
              <p className="mt-2 text-[10px] text-slate-500 font-medium">
                Sub Tradesman = one of your subs (Leo Cruz Ventures, DB Pest, etc.) picking up a WO. Pick their company from the dropdown — their existing sub portal will automatically show the WO so they upload photos + notes directly. Or use free-text for one-off help.
              </p>

              <div className="mt-5 flex flex-wrap items-end gap-3">
                <label className="block">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Date</span>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                    data-testid="lineup-date"
                    className="mt-1 h-11 px-2 border-2 border-[#0f172a] text-sm font-bold bg-white"/>
                </label>
                <label className="block">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Default slots</span>
                  <input type="number" min="1" max="24" step="0.5" value={capacity}
                    onChange={(e) => setCapacity(Number(e.target.value))}
                    data-testid="lineup-capacity"
                    className="mt-1 h-11 w-24 px-2 border-2 border-[#0f172a] text-sm font-bold bg-white"/>
                </label>
                <button onClick={generate} disabled={loading || crews.length === 0}
                  data-testid="lineup-generate"
                  className="tactile-btn h-11 px-5 bg-[#00C4CC] hover:bg-[#00a8af] text-white font-black uppercase text-xs tracking-widest disabled:opacity-60 inline-flex items-center gap-2 ml-auto">
                  <Lightning weight="fill" size={14}/> {loading ? "Drafting…" : "Draft Lineup"}
                </button>
              </div>
              <div className="mt-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                Turn=4 · Inspection=2 · Normal=1 · Small=½ · Big=3
              </div>
            </div>
          )}

          {/* STEP 3 — Preview */}
          {step === 3 && preview && (
            <>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button onClick={() => setStep(2)} data-testid="preview-back"
                  className="h-9 px-3 border-2 border-slate-300 text-slate-600 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1 hover:bg-slate-50">
                  <ArrowLeft weight="bold" size={12}/> Edit Trucks
                </button>
                <button onClick={generate} disabled={loading} data-testid="lineup-rescan"
                  title="Re-run the plan with the same trucks — triaged jobs stay off, freed slots fill with the next best jobs"
                  className="h-9 px-3 border-2 border-[#00C4CC] text-cyan-800 bg-cyan-50 hover:bg-cyan-100 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1 disabled:opacity-60">
                  <ArrowsClockwise weight="bold" size={12}/> {loading ? "Scanning…" : "Re-scan"}
                </button>
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider hidden sm:inline">⋮ or swipe left on a job to triage it off the radar</span>
                <button onClick={commit} disabled={committing}
                  data-testid="lineup-commit"
                  className="tactile-btn h-11 px-4 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-60 ml-auto">
                  <CheckCircle weight="fill" size={14}/> {committing ? "Committing…" : `Commit → ${date}`}
                </button>
              </div>

              {preview.leftover_open_jobs > 0 && (
                <div className="mt-3 rugged-card p-3 bg-amber-50 border-amber-400 flex items-center gap-2 text-sm font-bold text-amber-800">
                  <Warning weight="fill" size={16}/> {preview.leftover_open_jobs} open jobs left over — bump caps or pair a helper
                </div>
              )}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
                {preview.plans.map(p => {
                  const activeJobs = p.jobs.filter(j => !drops[j.id]);
                  const activeSlots = activeJobs.reduce((n, j) => n + j.slot_weight, 0);
                  const truckLabel = p.helper_name ? `${p.lead_name} + ${p.helper_name}` : p.lead_name;
                  return (
                    <div key={p.lead_id} className={`rugged-card p-4 min-w-0 overflow-hidden ${p.is_phantom ? "bg-fuchsia-50 border-fuchsia-400" : p.specialty === "painting" ? "bg-amber-50 border-amber-400" : "bg-white"}`} data-testid={`lineup-truck-${p.lead_id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        {p.is_phantom
                          ? <Ghost weight="fill" size={18} className="text-fuchsia-600"/>
                          : p.specialty === "painting" ? <PaintBrushBroad weight="fill" size={18} className="text-amber-700"/>
                          : p.helper_id ? <Truck weight="fill" size={18} className="text-[#00C4CC]"/>
                          : <User weight="fill" size={18}/>}
                        <h3 className="font-display font-black text-lg uppercase">{truckLabel || "Unassigned"}</h3>
                        {p.is_phantom && (
                          <span className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-fuchsia-600 text-white">Sub</span>
                        )}
                        {p.specialty === "painting" && (
                          <span className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-amber-500 text-white">Paint Only</span>
                        )}
                        <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 ${activeSlots > p.slots_max ? "bg-red-600 text-white" : "bg-[#0f172a] text-white"}`}>
                          {activeSlots.toFixed(1)} / {p.slots_max} slots
                        </span>
                      </div>
                      {(p.pinned_missing || []).length > 0 && (
                        <div className="mt-2 text-[10px] font-bold text-red-700 bg-red-50 border border-red-300 px-2 py-1" data-testid="pinned-missing">
                          📌 Not found / already placed: {p.pinned_missing.join(", ")}
                        </div>
                      )}
                      {p.jobs.length === 0 ? (
                        <div className="mt-3 text-xs font-bold text-slate-400 uppercase tracking-widest">
                          {p.specialty === "painting" ? "— No paint jobs match" : "— No jobs suggested"}
                        </div>
                      ) : (
                        <div className="mt-3 space-y-1.5">
                          {p.jobs.map((j, ji) => {
                            const dropped = !!drops[j.id];
                            const peek = ji === 0 && p.lead_id === preview.plans[0]?.lead_id && showPeek;
                            return (
                              <SweepJobRow key={j.id} j={j} dropped={dropped} peek={peek}
                                onToggle={() => setDrops({ ...drops, [j.id]: !dropped })}
                                onTriaged={() => removeFromPreview(j.id)}>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-black uppercase flex items-center gap-1.5 flex-wrap min-w-0">
                                    {j.work_order_number && <span className="font-mono text-[9px] bg-[#0f172a] text-white px-1">WO#{j.work_order_number}</span>}
                                    <span className={`text-[9px] px-1 font-black uppercase ${j.priority === "urgent" ? "bg-red-600 text-white" : j.priority === "high" ? "bg-orange-500 text-white" : "bg-slate-200 text-slate-700"}`}>{j.priority}</span>
                                    <span className="text-[9px] bg-fuchsia-600 text-white px-1 font-black">{j.slot_weight}s</span>
                                    {j.category && <span className="text-[9px] bg-slate-600 text-white px-1 font-black uppercase">{j.category}</span>}
                                    {j.weather_preference === "exterior" && preview.weather?.label === "rainy" && (
                                      <span title="Exterior job on a rainy day — consider deferring" data-testid={`weather-warn-${j.id}`}
                                        className="text-[9px] bg-red-600 text-white px-1 font-black uppercase inline-flex items-center gap-0.5">☔ Rain-Risk</span>
                                    )}
                                    {j.weather_preference === "exterior" && preview.weather?.label === "sunny" && (
                                      <span title="Exterior job on a sunny day — good fit" data-testid={`weather-good-${j.id}`}
                                        className="text-[9px] bg-emerald-600 text-white px-1 font-black uppercase inline-flex items-center gap-0.5">☀ Sunny-Fit</span>
                                    )}
                                    {j.weather_preference === "interior" && preview.weather?.label === "rainy" && (
                                      <span title="Interior job — good pick for a rainy day" data-testid={`weather-indoor-${j.id}`}
                                        className="text-[9px] bg-sky-600 text-white px-1 font-black uppercase inline-flex items-center gap-0.5">🏠 Indoor-Fit</span>
                                    )}
                                    {j.pinned && <span className="text-[9px] bg-amber-400 text-slate-900 px-1 font-black uppercase" data-testid={`lineup-pinned-${j.id}`}>📌 Pinned</span>}
                                    <ReturnBadge rv={j.return_visit} testId={`lineup-return-${j.id}`}/>
                                    {j.portfolio_name && (
                                      <span className="text-[9px] text-white px-1 font-black uppercase" style={{ background: j.portfolio_color || "#0f172a" }}
                                        title={j.portfolio_access_notes || j.portfolio_name} data-testid={`lineup-portfolio-${j.id}`}>{j.portfolio_name}</span>
                                    )}
                                    <Link to={`/admin/jobs/${j.id}`} className="basis-full min-w-0 max-w-full truncate hover:underline normal-case font-bold" title={j.title}>{j.title}</Link>
                                  </div>
                                  <div className="text-[10px] text-slate-600 mt-0.5 truncate flex items-center gap-1">
                                    <MapPin weight="bold" size={10}/> {j.property_address}{j.unit ? ` · #${j.unit}` : ""}
                                    {j.portfolio_access_notes && <span className="text-amber-800 font-bold">· 🔑 {j.portfolio_access_notes}</span>}
                                  </div>
                                </div>
                              </SweepJobRow>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </AdminShell>
  );
}

function StepPill({ num, label, active, done, onClick }) {
  const cls = active ? "bg-[#00C4CC] text-white border-[#00a8af]"
    : done ? "bg-[#0f172a] text-white border-[#0f172a]"
    : "bg-white text-slate-500 border-slate-300";
  return (
    <button onClick={onClick}
      data-testid={`step-pill-${num}`}
      className={`px-3 h-9 border-2 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-2 ${cls}`}>
      <span className={`h-6 w-6 rounded-full inline-flex items-center justify-center text-xs ${active || done ? "bg-white/20" : "bg-slate-100"}`}>{done ? <CheckCircle weight="fill" size={14}/> : num}</span>
      {label}
    </button>
  );
}

const WEATHER_ICON = { sun: Sun, cloud: Cloud, "cloud-sun": Cloud, rain: CloudRain, snow: CloudSnow, storm: CloudLightning };

function WeatherStrip({ weather, date, setDate, crews, preview }) {
  const days = weather.days.slice(0, 7);
  const selected = days.find(d => d.date === date);
  // Warning: if selected day is wet AND any truck/plan has painting or a paint/roof job
  const hasPaintOrRoof = () => {
    if (preview && preview.plans) {
      return preview.plans.some(p => p.specialty === "painting" || p.jobs?.some(j =>
        /paint|roof|drywall|stucco|exterior/i.test((j.category || "") + " " + (j.title || ""))));
    }
    return crews.some(c => c.specialty === "painting");
  };
  const showWarning = selected?.is_wet && hasPaintOrRoof();
  return (
    <div className="mt-4 rugged-card p-3 bg-white" data-testid="lineup-weather">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">7-Day Forecast · {weather.location?.label}</span>
        {selected && (
          <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-700">
            Planning for {new Date(date + 'T12:00:00').toLocaleDateString(undefined, {weekday:'short', month:'short', day:'numeric'})}: {selected.label} · {selected.hi}°/{selected.lo}° · {selected.precip_prob}% rain
          </span>
        )}
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {days.map(d => {
          const Icon = WEATHER_ICON[d.icon] || Cloud;
          const isSelected = d.date === date;
          const isWet = d.is_wet;
          return (
            <button key={d.date} onClick={() => setDate(d.date)} data-testid={`weather-${d.date}`}
              className={`flex-shrink-0 flex flex-col items-center gap-0.5 px-2 py-1.5 border-2 min-w-[68px] ${isSelected ? "bg-[#0f172a] text-white border-[#0f172a]" : isWet ? "bg-blue-50 border-blue-300 text-slate-700 hover:bg-blue-100" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>
              <span className="text-[9px] font-black uppercase tracking-widest">{new Date(d.date + 'T12:00:00').toLocaleDateString(undefined, {weekday:'short'})}</span>
              <Icon weight={isSelected ? "fill" : "bold"} size={18} className={isWet ? "text-blue-500" : d.icon === "sun" ? "text-amber-500" : ""}/>
              <span className="text-[10px] font-black">{d.hi}°</span>
              {d.precip_prob > 20 && <span className={`text-[9px] font-bold ${d.precip_prob >= 60 ? "text-blue-600" : "text-slate-500"}`}>{d.precip_prob}%</span>}
            </button>
          );
        })}
      </div>
      {showWarning && (
        <div className="mt-2 bg-amber-50 border-2 border-amber-500 p-2 flex items-start gap-2" data-testid="weather-warning">
          <Warning weight="fill" size={16} className="text-amber-600 flex-shrink-0 mt-0.5"/>
          <div className="text-xs text-amber-900 font-bold">
            Heads up — {selected?.label?.toLowerCase()} on {new Date(date + 'T12:00:00').toLocaleDateString(undefined, {month:'short', day:'numeric'})} with paint/roof/exterior work planned. Consider moving those jobs to a dry day.
          </div>
        </div>
      )}
    </div>
  );
}
